import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Plus, Edit, Trash2, FileText, X, Upload } from 'lucide-react';
import { toast } from 'sonner';

export default function GestionContratosResoluciones() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('contratos');
  const [contratos, setContratos] = useState([]);
  const [resoluciones, setResoluciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    numero: '',
    nombre: '',
    descripcion: '',
    fecha: '',
    archivo_url: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contratosRes, resolucionesRes] = await Promise.all([
        fetch(`${API_URL}/api/contratos`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/resoluciones`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (contratosRes.ok) setContratos(await contratosRes.json());
      if (resolucionesRes.ok) setResoluciones(await resolucionesRes.json());
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const endpoint = activeTab === 'contratos' ? 'contratos' : 'resoluciones';
    const method = editingItem ? 'PUT' : 'POST';
    const url = editingItem 
      ? `${API_URL}/api/${endpoint}/${editingItem.id}`
      : `${API_URL}/api/${endpoint}`;

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar');
      }

      toast.success(editingItem ? 'Actualizado correctamente' : 'Creado correctamente');
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este registro?')) return;

    const endpoint = activeTab === 'contratos' ? 'contratos' : 'resoluciones';

    try {
      const response = await fetch(`${API_URL}/api/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al eliminar');

      toast.success('Eliminado correctamente');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      numero: item.numero,
      nombre: item.nombre,
      descripcion: item.descripcion || '',
      fecha: item.fecha || '',
      archivo_url: item.archivo_url || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ numero: '', nombre: '', descripcion: '', fecha: '', archivo_url: '' });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/upload/document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadFormData
      });

      if (!response.ok) throw new Error('Error al subir archivo');

      const data = await response.json();
      setFormData(prev => ({ ...prev, archivo_url: data.url }));
      toast.success('Archivo subido');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const currentItems = activeTab === 'contratos' ? contratos : resoluciones;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">Gestión de Contratos y Resoluciones</h1>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-slate-300 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
            <span>Volver</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-6">
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveTab('contratos')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'contratos' 
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
              data-testid="tab-contratos"
            >
              Contratos ({contratos.length})
            </button>
            <button
              onClick={() => setActiveTab('resoluciones')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'resoluciones' 
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
              data-testid="tab-resoluciones"
            >
              Resoluciones ({resoluciones.length})
            </button>
          </div>
        </div>

        {/* Add Button */}
        <div className="flex justify-end mb-6">
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="btn-nuevo"
          >
            <Plus className="h-5 w-5" />
            Nuevo {activeTab === 'contratos' ? 'Contrato' : 'Resolución'}
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Cargando...</div>
          ) : currentItems.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              No hay {activeTab} registrados
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0F172A] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Número</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Nombre</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Fecha</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Archivo</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {currentItems.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.numero}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{item.nombre}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {item.fecha ? new Date(item.fecha).toLocaleDateString('es-CO') : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {item.archivo_url ? (
                          <a 
                            href={`${API_URL}${item.archivo_url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <FileText className="h-4 w-4" />
                            Ver PDF
                          </a>
                        ) : (
                          <span className="text-slate-400">Sin archivo</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button 
                            onClick={() => openEditModal(item)} 
                            className="p-1 text-slate-500 hover:text-blue-600"
                            title="Editar"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button 
                            onClick={() => handleDelete(item.id)} 
                            className="p-1 text-slate-500 hover:text-red-600"
                            title="Eliminar"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editingItem ? 'Editar' : 'Nuevo'} {activeTab === 'contratos' ? 'Contrato' : 'Resolución'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Número <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.numero}
                  onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={activeTab === 'contratos' ? 'Ej: CONT-2024-001' : 'Ej: RES-2024-001'}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre descriptivo"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Descripción opcional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha</label>
                <input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Archivo PDF</label>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
                {formData.archivo_url && (
                  <p className="text-xs text-green-600 mt-1">Archivo cargado: {formData.archivo_url.split('/').pop()}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingItem ? 'Guardar Cambios' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
